import { For, Icon, If, onMount, reactive } from 'cdui-js';
import { DiFiNavBar } from '../../../../components/DiFi/DiFiNavBar';
import { loadAgreementDocsByCodesWithPreload } from '../../../../lib/agreement-pdf-cache';
import type { AgreementSceneDocItem } from '../../../../api/agreement-doc';
import { openAgreementDoc } from '../../../../lib/agreement-doc-view';
import { agreementLinkLabel } from '../../../../lib/agreementLinkLabel';
import i18n from '../../../../i18n';
import { isLegalEmbeddedInApp } from '../../../legal/legalEmbed';
import { isH5User } from '../../../../stores/user';
import { pState } from './store';
import { routeBackFromOpenAccountDocs } from './openAccountDocsNav';

const SCENE_OPEN_ACCOUNT_TIP = 'SCENE_OPEN_ACCOUNT_TIP';

type OpenAccountDocRow = {
  docCode: string;
  /** 展示名（接口无 name 时兜底） */
  fallbackName: () => string;
};

/** 开户文件列表（不含外层 AGR002） */
const OPEN_ACCOUNT_DOCS: OpenAccountDocRow[] = [
  { docCode: 'AGR0015', fallbackName: () => i18n.OpenProgress.Risk.Sign.DocClientAgreement },
  { docCode: 'AGR0018', fallbackName: () => i18n.OpenProgress.Risk.Sign.DocClientDeclaration },
  { docCode: 'AGR0019', fallbackName: () => i18n.OpenProgress.Risk.Sign.DocTaxSelfCert },
  { docCode: 'AGR0020', fallbackName: () => i18n.OpenProgress.Risk.Sign.DocFeeNotice },
  { docCode: 'AGR003', fallbackName: () => i18n.OpenProgress.Risk.Sign.DocDerivativesKnowledge },
  { docCode: 'AGR009', fallbackName: () => i18n.OpenProgress.Risk.Sign.DocStandingAuth },
];

export const OpenAccountDocs = () => {
  // App / 内嵌：原生顶栏负责返回，不叠 H5 DiFiNavBar（与开户主流程、协议详情一致）
  const showH5Nav = isH5User() && !isLegalEmbeddedInApp();

  const state = reactive({
    docMap: {} as Record<string, AgreementSceneDocItem>,
  });

  onMount(() => {
    loadAgreementDocsByCodesWithPreload({
      sceneCode: SCENE_OPEN_ACCOUNT_TIP,
      docCodes: OPEN_ACCOUNT_DOCS.map((d) => d.docCode),
    })
      .then(({ docMap }) => {
        state.docMap = docMap;
      })
      .catch(() => {
        state.docMap = {};
      });
  });

  const goBackToOpenProgress = () => routeBackFromOpenAccountDocs(pState.current);

  const openRow = (row: OpenAccountDocRow) => {
    openAgreementDoc(state.docMap[row.docCode] ?? null);
  };

  const displayName = (row: OpenAccountDocRow) => state.docMap[row.docCode]?.name || row.fallbackName();

  const pageClass = showH5Nav
    ? 'open-account-docs page-wrapper-with-header flex-column bg-c height-100'
    : 'open-account-docs page-wrapper-no-header flex-column bg-c height-100';

  return (
    <div class={pageClass} style={{ width: '100%', overflow: 'hidden' }}>
      <If when={showH5Nav}>
        <DiFiNavBar
          title={i18n.OpenProgress.Prepare.OpenAccountDocsTitle}
          fixed
          class="bg-c"
          right={<div class=""></div>}
          onClickLeft={goBackToOpenProgress}
        />
      </If>

      <div
        class="flex-1 no-scrollbar padding-x-L"
        style={{
          width: '100%',
          'min-width': 0,
          'overflow-x': 'hidden',
          'overflow-y': 'auto',
          'padding-top': showH5Nav ? '32px' : '16px',
          'padding-bottom': '100px',
          'box-sizing': 'border-box',
        }}
      >
        <For each={OPEN_ACCOUNT_DOCS}>
          {(row) => (
            <div
              class="flex justify-space-between align-center border-b border-b-c-L"
              style={{
                height: '56px',
                width: '100%',
                'min-width': 0,
                'box-sizing': 'border-box',
                cursor: 'pointer',
              }}
              onclick={() => openRow(row)}
            >
              {/* base.css 有 * { flex: none }，须显式 flex:1 + min-width:0 才能收缩省略 */}
              <div class="agreement-link font-s single-line-ellipsis" style={{ flex: 1, 'min-width': 0, cursor: 'pointer' }}>
                {agreementLinkLabel(state.docMap[row.docCode]?.name, row.fallbackName())}
              </div>
              <Icon name="common-arrow-right" class="icon-s-S icon-c-D flex-shrink-0" />
            </div>
          )}
        </For>
      </div>

      <div
        class="fixed position-b position-l width-100 bg-c padding-x-L page-foot"
        style={{ 'padding-top': '12px', 'z-index': 99, 'box-sizing': 'border-box' }}
      >
        <button class="primary-btn btn-rounded width-100" style={{ height: '50px' }} onclick={goBackToOpenProgress}>
          {i18n.OpenProgress.Prepare.ReadAndAgree}
        </button>
      </div>
    </div>
  );
};

export default OpenAccountDocs;
